/**
 * Resource Tags list page
 */

import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Moon, Plus, RefreshCw, Search, Sun } from 'lucide-react'

import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import {
  ResourceTagsTable,
  ResourceTagsEmpty,
  DeleteResourceTagDialog,
} from '@/components/resource-tags'
import { useResourceTags, useDeleteResourceTag } from '@/hooks/use-resource-tags'
import { useThemeStore } from '@/stores/theme-store'
import { cn } from '@/lib/utils'
import type { ResourceTagListItem } from '@/types/resource-tag'

const DEFAULT_PAGE_SIZE = 10

export function ResourceTagsPage() {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useThemeStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [deletingTag, setDeletingTag] = useState<ResourceTagListItem | null>(null)

  const { data, isLoading, isError, error, refetch, isFetching } = useResourceTags({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: searchQuery || undefined,
  })

  const deleteMutation = useDeleteResourceTag()

  const items = data?.items ?? []
  const pagination = data?.pagination
  const showEmpty = !isLoading && items.length === 0

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setPage(1)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingTag) return
    try {
      await deleteMutation.mutateAsync(deletingTag.id)
      setDeletingTag(null)
    } catch {
      // toast handled in hook
    }
  }, [deletingTag, deleteMutation])

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Data Classification</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-8" onClick={() => navigate('/pac/resource-tags/new')}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            New Classification
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('mr-2 h-3.5 w-3.5', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
        <p className="text-sm text-muted-foreground">
          Map application resource field values to semantic classifications used in
          access policies.
        </p>

        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : 'Failed to load data classifications'}
            </AlertDescription>
          </Alert>
        )}

        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search classifications…"
            className="pl-8"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        {showEmpty ? (
          <ResourceTagsEmpty
            hasSearch={!!searchQuery}
            onCreateClick={() => navigate('/pac/resource-tags/new')}
          />
        ) : (
          <>
            <ResourceTagsTable
              items={items}
              isLoading={isLoading}
              onView={(tag) => navigate(`/pac/resource-tags/${tag.id}`)}
              onEdit={(tag) => navigate(`/pac/resource-tags/${tag.id}?mode=edit`)}
              onDelete={setDeletingTag}
            />

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Page {pagination.page} of {pagination.totalPages} ({pagination.totalItems}{' '}
                  total)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <DeleteResourceTagDialog
        open={!!deletingTag}
        onOpenChange={(open) => !open && setDeletingTag(null)}
        resourceTag={deletingTag?.resourceTag ?? ''}
        tagValue={deletingTag?.tagValue ?? ''}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </>
  )
}
